import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, AlertTriangle, Info, CreditCard, Sparkles, FileText } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ContentFormData } from '..';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useCallback } from 'react';
import { useCredits } from '@/contexts/CreditContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { findCatalogModel, useModelCatalog } from '@/hooks/useModelCatalog';
import { usePresets } from '@/hooks/usePresets';
import { voices } from '@/data/voices';

interface PreviewProps {
  formData: ContentFormData;
  onComplete?: (callback: () => Promise<number>) => void;
  onCreateContent: () => Promise<number>;
}

export function Preview({ formData, onCreateContent, onComplete }: PreviewProps) {
  const [isCreating, setIsCreating] = useState(false);
  const { refetch: refetchCredits } = useCredits();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: modelCatalog } = useModelCatalog();
  const { data: presetCatalog } = usePresets();
  const presetName = presetCatalog?.presets.find(
    preset => preset.id === formData.presetId,
  )?.name;

  const platformName = {
    tiktok: 'TikTok',
    instagram: 'Instagram Reels',
    shorts: 'YouTube Shorts',
    youtube: 'YouTube',
    video: 'Video'
  }[formData.platform || ''] || 'Unknown Platform';

  const isVerticalOrientation = (platform: string): boolean => {
    return ['tiktok', 'instagram', 'shorts'].includes(platform);
  };

  const voiceName = voices.find(voice => voice.id === formData.voice)?.name
    || 'Unknown Voice';

  const languageName = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
  }[formData.language || ''] || 'Unknown Language';

  const styleName = {
    balanced: 'Balanced',
    informative: 'Informative',
    conversational: 'Conversational',
    storytelling: 'Storytelling',
    inspirational: 'Inspirational',
    humorous: 'Humorous',
    dramatic: 'Dramatic',
    technical: 'Technical',
    default: 'Default'
  }[formData.style || ''] || 'Default';

  const scenesEstimate = formData.sceneCount;
  const selectedModel = findCatalogModel(modelCatalog?.groups, formData.visualModel);
  const selectedGroup = modelCatalog?.groups.find(group => group.id === formData.visualModel);
  const minimumCredits = scenesEstimate * (
    selectedModel?.credits_per_scene ?? selectedGroup?.min_credits_per_scene ?? 0
  );
  const maximumCredits = scenesEstimate * (
    selectedModel?.credits_per_scene ?? selectedGroup?.max_credits_per_scene ?? 0
  );
  const creditsRequired = minimumCredits === maximumCredits
    ? `${minimumCredits} credits`
    : `${minimumCredits}-${maximumCredits} credits`;

  const getTopicDisplay = () => {
    if (formData.topic === 'custom') {
      return formData.topicName || 'Untitled Topic';
    }
    return formData.topic.charAt(0).toUpperCase() + formData.topic.slice(1);
  };

  const getOptimizationTips = () => {
    const tips = [];
    
    if (isVerticalOrientation(formData.platform)) {
      if (formData.duration > 60) {
        tips.push({
          type: 'warning',
          text: 'Vertical videos perform best when under 60 seconds',
        });
      }
    } else {
      if (formData.duration < 30) {
        tips.push({
          type: 'info',
          text: 'Consider longer durations for landscape videos to maximise engagement',
        });
      }
    }

    tips.push({
      type: 'success',
      text: `Your video length is optimised for ${platformName}`,
    });

    tips.push({
      type: 'info',
      text: 'Visual variety in shot composition will keep viewers engaged throughout your video',
    });

    if (!formData.email) {
      tips.push({
        type: 'warning',
        text: 'Adding an email will help you receive notifications when your video is ready',
      });
    }

    return tips;
  };

  const optimizationTips = getOptimizationTips();

  const getModelDisplay = () => {
    const styleName = selectedModel?.styles?.find(
      style => style.id === formData.recraftStyle,
    )?.name;
    if (selectedModel) {
      const base = `${selectedModel.name} (${selectedModel.tier})`;
      return styleName ? `${base} · ${styleName}` : base;
    }
    if (selectedGroup) return selectedGroup.name;
    return formData.visualModel || 'Unknown visual model';
  };
  
  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    try {
      const contentId = await onCreateContent();
      refetchCredits();
      setIsCreating(false);
      return contentId;
    } catch (error: any) {
      setIsCreating(false);
      if (error.response?.status === 402) {
        const detail = error.response.data.detail || {};
        toast({
          title: 'Insufficient Credits',
          description: `You need ${detail.required || 'more'} credits, but you only have ${detail.available || 0} credits available. Please upgrade your plan to continue.`,
          variant: 'destructive',
          action: (
            <Button
              variant="secondary"
              size="sm"
              className="text-blue-500 bg-white hover:bg-gray-50"
              onClick={() => navigate('/pricing')}
            >
              Upgrade Plan
            </Button>
          ),
          duration: 10000,
        });
      }
      throw error;
    }
  }, [onCreateContent, refetchCredits, navigate, toast]);

  useEffect(() => {
    if (onComplete) {
      onComplete(handleCreate);
    }
  }, [onComplete, handleCreate]);

  return (
    <div className="max-w-[800px] mx-auto space-y-6 md:space-y-8">
      <Card className="overflow-hidden border-muted/60 shadow-sm">
        <div className="p-4 md:p-6 space-y-8">
          {/* Basic Content Details */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Content Details
            </h3>
            
            <div className="space-y-4">
              <div className="grid gap-3">
                <PreviewItem label="Platform" value={platformName} />
                {formData.socialHandle && (
                  <PreviewItem label="Social Handle" value={formData.socialHandle} />
                )}
                <PreviewItem label="Topic" value={getTopicDisplay()} />
                {presetName && <PreviewItem label="Preset" value={presetName} />}
                <PreviewItem label="Visual Style" value={getModelDisplay()} />
              </div>
            </div>
          </div>

          {/* Narrative and Creative Direction */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              Creative Vision
            </h3>
            
            <div className="space-y-4">
              {formData.style && (
                <Card className="p-4 bg-muted/5">
                  <h4 className="text-sm font-medium mb-2">Narrative Style</h4>
                  <div className="text-sm whitespace-pre-line">{formData.style}</div>
                </Card>
              )}

              {formData.styleNotes && (
                <Card className="p-4 bg-muted/5">
                  <h4 className="text-sm font-medium mb-2">Creative Direction</h4>
                  <div className="text-sm whitespace-pre-line">{formData.styleNotes}</div>
                </Card>
              )}
            </div>
          </div>

          {/* Voice and Duration */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Info className="h-4 w-4 text-primary" />
              Technical Details
            </h3>
            
            <div className="grid gap-3">
              <PreviewItem label="Voice" value={voiceName} />
              <PreviewItem label="Language" value={languageName} />
              <PreviewItem 
                label="Duration" 
                value={`${formData.duration} seconds (${formData.sceneCount} scenes × ${formData.sceneLength}s each)`}
              />
              {formData.email && (
                <PreviewItem label="Notification Email" value={formData.email} />
              )}
            </div>
          </div>

          {/* Credit Information */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-primary" />
              Estimated Cost
            </h3>
            
            <div className="bg-primary/5 rounded-lg p-4 space-y-4">
              <div className="grid gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>Estimated credits required:</span>
                  <Badge variant="secondary" className="text-base">
                    {creditsRequired}
                  </Badge>
                </div>
              </div>
              
              <Alert variant="default" className="bg-background">
                <AlertDescription className="text-sm">
                  This is just an estimate. Credits will only be deducted when you generate your content after finalising your storyboard.
                </AlertDescription>
              </Alert>
            </div>
          </div>

          {/* Production Notes */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Info className="h-4 w-4 text-primary" />
              Production Notes
            </h3>
            
            <div className="space-y-2">
              {optimizationTips.map((tip, index) => (
                <Alert 
                  key={index} 
                  variant={tip.type === 'warning' ? 'destructive' : 'default'}
                  className="py-2"
                >
                  <AlertDescription className="flex items-center text-sm">
                    {tip.type === 'warning' ? (
                      <AlertTriangle className="h-4 w-4 mr-2 shrink-0" />
                    ) : tip.type === 'info' ? (
                      <Info className="h-4 w-4 mr-2 shrink-0" />
                    ) : (
                      <Check className="h-4 w-4 mr-2 shrink-0" />
                    )}
                    {tip.text}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function PreviewItem({ 
  label, 
  value, 
  fullWidth = false,
}: { 
  label: string; 
  value: string; 
  fullWidth?: boolean;
}) {
  return (
    <div className="flex justify-between items-start gap-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      {fullWidth ? (
        <div className="flex-1">
          <div className="text-sm whitespace-pre-line text-right">{value}</div>
        </div>
      ) : (
        <Badge variant="secondary" className="shrink-0">{value}</Badge>
      )}
    </div>
  );
}
